import { Box, Heading, HStack, Image, Text, VStack } from "@chakra-ui/react";
import { ComponentProps } from "react";

import { CompletionInfo, UsersCompleted } from "../../components/quest-card";

import { Countdown } from "@/components/countdown";
import { HoverCardWithLink } from "@/components/hover-card-with-link";
import { AppRoute } from "@/constants/shared";
import { Quest } from "@/types";

import xpPathSvg from "/assets/leaderboard/pp.svg";

export const LatestQuestCard = ({ quest }: { quest: Quest }) => {
  const isAllCompleted = quest.tasks.every((task) => task.completed);
  const percentCompleted = (
    (quest.tasks.reduce((acc, task) => acc + (task.completed ? 1 : 0), 0) / quest.tasks.length) *
    100
  ).toFixed(0);
  const assetSrc = `/assets/quests/wave-${quest.wave}.avif`;

  return (
    <HoverCardWithLink
      href={`${AppRoute.Quests}/${quest.id}`}
      linkProps={{
        w: "full",
        h: "full",
      }}
      cardRootStyles={{
        p: 0,
        display: "flex",
        flexDirection: {
          base: "column",
          lg: "row",
        },
        justifyContent: "space-between",
      }}
    >
      <Image src={assetSrc} alt="Quest" aspectRatio={[236 / 165, 236 / 165, 236 / 165, 250 / 160]} hideFrom="lg" />
      <VStack gap={4} alignItems="flex-start" p={[4, 4, 4, 8]}>
        <VStack gap={2} alignItems="flex-start">
          <Heading fontSize="2xl" fontWeight="bold" lineHeight="32px">
            {quest.title}
          </Heading>
          <Text fontWeight="500" color="text.secondary" lineHeight="150%" whiteSpace="pre-line">
            {quest.description}
          </Text>
        </VStack>
        <UsersCompleted numUsersCompleted={quest.numUsersCompleted ?? 0} />
        <HStack gap={8} mt="auto">
          <XPBadge xpReward={Number(quest.xpReward)} />
          <CompletionInfo percentCompleted={Number(percentCompleted)} isAllCompleted={isAllCompleted} size="large" />
        </HStack>
      </VStack>
      <Box position="relative" minH="full" flex={1} aspectRatio={419 / 293} minW={420} hideBelow="lg">
        <Image
          src={assetSrc}
          alt="Quest"
          position="absolute"
          w="100%"
          h="100%"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          objectFit="cover"
          objectPosition="center"
        />
        {quest.isDoubleRewards && <DoubleRewardsInfo endDate={quest.doubleRewardsEndTime} />}
      </Box>
    </HoverCardWithLink>
  );
};

export const XPBadge = ({ xpReward }: { xpReward: number }) => {
  return (
    <HStack gap={2.5} px={1.5} pr={3} py={2.5} rounded="full" border="1px solid #F0F0F0">
      <Image src="/assets/pp-circle.svg" alt="PP" />
      <Text fontSize="sm" fontWeight="500" lineHeight="20px">
        {xpReward}
      </Text>
    </HStack>
  );
};

export const DoubleRewardsInfo = ({ endDate, ...props }: { endDate: Date } & ComponentProps<typeof HStack>) => {
  return (
    <HStack
      position="absolute"
      left={0}
      right={0}
      bottom={0}
      bg="#004038"
      px={6}
      py={4}
      textAlign="center"
      color="white"
      gap={0}
      justifyContent="center"
      alignItems="center"
      {...props}
    >
      <Countdown date={endDate} fontSize="14px" fontWeight="bold" ml={1} mr={2} />
      <Image src={xpPathSvg} boxSize={4} mr={3} />
      <Text fontSize="sm" fontWeight="500" lineHeight="20px" color="fg.inverted">
        2x Plume Points
      </Text>
    </HStack>
  );
};
